import { useEffect, useMemo, useState } from "react";
import {
  Button,
  DateTimePicker,
  Input,
  Modal,
  Select,
  TextArea,
} from "../../../shared";
import {
  useAddTemplateMutation,
  useUpdateTemplateMutation,
} from "../../../redux/services/templates/api";
import { useGetTaskTemplateGroupsQuery } from "../../../redux/services/tasks/tasksApi";
import { PRIORITY_OPTIONS } from "../../../constants/select";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks";
import { ClientsDropdown } from "../../../components/ClientsDropdown";
import { TEMPLATE_TYPES } from "./template.constants";
import {
  buildTemplatePayload,
  convertHoursToMilliseconds,
  convertMillisecondsToHours,
  getTemplateContent,
} from "./template.utils";

const emptyItem = () => ({
  _id: Math.random().toString(36).slice(2, 11),
  priority: "low",
  serviceType: "",
  quotedHours: 0,
  summary: "",
  parts: [],
});

const defaultForm = {
  name: "",
  description: "",
  client: null,
  priority: "low",
  summary: "",
  orderNumber: "",
  loadNumber: "",
  transactionDate: "",
  originalCall: "",
  useGlobalRates: false,
  useFlatRate: false,
  flatRate: "",
  baseRate: "",
  fuelRate: "",
};

const rateSourceOptions = [
  { label: "Client Rates", value: "client" },
  { label: "Global Rates", value: "global" },
];

const JobTemplateModal = ({ visible, template, onClose, mode = "create" }) => {
  const { appType } = useConfig();
  const isTrucking = appType === "trucking";
  const [form, setForm] = useState(defaultForm);
  const [items, setItems] = useState([emptyItem()]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const { data: groups = [] } = useGetTaskTemplateGroupsQuery({
    appliesTo: "job",
  });
  const [addTemplate, { isLoading: isAdding }] = useAddTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateTemplateMutation();

  const isLoading = isAdding || isUpdating;
  const isEdit = mode === "edit" && template?._id;

  useEffect(() => {
    if (!visible) return;

    const content = getTemplateContent(template);
    const defaults = content.defaults || content;
    const templateItems = content.items || template?.items || [];

    setForm({
      ...defaultForm,
      name:
        mode === "duplicate" && template?.name
          ? `Copy of ${template.name}`
          : template?.name || "",
      description: template?.description || "",
      client: defaults.client || null,
      priority: defaults.priority || "low",
      summary: defaults.summary || "",
      orderNumber: defaults.orderNumber || "",
      loadNumber: defaults.loadNumber || "",
      transactionDate: defaults.transactionDate || "",
      originalCall: defaults.originalCall || "",
      useGlobalRates: !!defaults.useGlobalRates,
      useFlatRate: !!defaults.useFlatRate,
      flatRate: defaults.flatRate || "",
      baseRate: defaults.baseRate || "",
      fuelRate: defaults.fuelRate || "",
    });
    setItems(
      templateItems.length
        ? convertMillisecondsToHours(templateItems)
        : [emptyItem()]
    );
    setSelectedGroups(content.taskTemplateGroups || []);
  }, [visible, template, mode]);

  const groupOptions = useMemo(
    () =>
      groups.filter((group) => {
        const appliesToMatch = ["job", "both"].includes(
          group.appliesTo || "both"
        );
        const appTypeMatch = !group.appType || group.appType === "all" || group.appType === appType;
        return appliesToMatch && appTypeMatch;
      }),
    [groups, appType]
  );

  const handleChange = (id, value) => {
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleClientFormChange = ({ id, value }) => {
    handleChange(id, value);
  };

  const handleItemChange = (itemId, id, value) => {
    setItems((prev) =>
      prev.map((item) => (item._id === itemId ? { ...item, [id]: value } : item))
    );
  };

  const handleToggleGroup = (groupKey) => {
    setSelectedGroups((prev) =>
      prev.includes(groupKey)
        ? prev.filter((key) => key !== groupKey)
        : [...prev, groupKey]
    );
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      toast.error("Template name is required");
      return;
    }

    const defaults = {
      priority: form.priority,
      summary: form.summary,
      client: form.client,
      orderNumber: form.orderNumber,
      loadNumber: form.loadNumber,
      transactionDate: form.transactionDate,
      originalCall: form.originalCall,
      useGlobalRates: form.useGlobalRates,
      useFlatRate: form.useFlatRate,
      flatRate: form.flatRate,
      baseRate: form.baseRate,
      fuelRate: form.fuelRate,
    };

    const payload = buildTemplatePayload({
      type: TEMPLATE_TYPES.JOB,
      form,
      defaults,
      items: isTrucking ? [] : convertHoursToMilliseconds(items),
      taskTemplateGroups: selectedGroups,
      appType,
    });

    try {
      const response = isEdit
        ? await updateTemplate({ id: template._id, data: payload }).unwrap()
        : await addTemplate(payload).unwrap();

      if (response.status !== "success") {
        toast.error("Failed to save template");
        return;
      }

      toast.success("Template saved successfully");
      onClose?.();
    } catch (error) {
      console.error("Save job template error:", error);
      toast.error(error.data?.message || "Failed to save template");
    }
  };

  return (
    <Modal
      title={isEdit ? `Edit ${isTrucking ? "Load" : "Job"} Template` : `Create ${isTrucking ? "Load" : "Job"} Template`}
      visible={visible}
      onClose={onClose}
      confirm={handleSubmit}
      loading={isLoading}
      size="lg"
      submitText="Save Template"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Template Name"
            value={form.name}
            onChange={(e) => handleChange("name", e.value)}
            placeholder={
              isTrucking ? "Standard load template" : "Standard install job"
            }
            required
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => handleChange("description", e.value)}
            placeholder="When this template should be used"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ClientsDropdown
            form={form}
            updateForm={handleClientFormChange}
            value={form.client}
            hideFooter
            onClick={(client) => handleChange("client", client)}
            onChange={(client) => handleChange("client", client)}
          />
          <Select
            label="Rate Source"
            value={form.useGlobalRates ? "global" : "client"}
            options={rateSourceOptions}
            onChange={(e) => handleChange("useGlobalRates", e.value === "global")}
          />
        </div>

        {isTrucking ? (
          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div className="flex justify-between items-center border-b border-neutral-300 pb-2">
              <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                Load Information
              </h3>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.useFlatRate}
                  onChange={(e) =>
                    handleChange("useFlatRate", e.target.checked)
                  }
                />
                Use Flat Rate
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Load Number"
                value={form.loadNumber}
                onChange={(e) => handleChange("loadNumber", e.value)}
                placeholder="Optional default"
              />
              <DateTimePicker
                id="transactionDate"
                labels={{
                  date: "Transaction Date",
                  time: "Transaction Time",
                }}
                placeholder="Optional default date and time"
                value={form.transactionDate || ""}
                onChange={(value) => handleChange("transactionDate", value)}
                size="sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {form.useFlatRate ? (
                <Input
                  label="Flat Rate"
                  type="number"
                  value={form.flatRate}
                  onChange={(e) => handleChange("flatRate", e.value)}
                  placeholder="0.00"
                />
              ) : (
                <>
                  <Input
                    label="Base Rate"
                    type="number"
                    value={form.baseRate}
                    onChange={(e) => handleChange("baseRate", e.value)}
                    placeholder="0.00"
                  />
                  <Input
                    label="Fuel Rate"
                    type="number"
                    value={form.fuelRate}
                    onChange={(e) => handleChange("fuelRate", e.value)}
                    placeholder="0.00"
                  />
                </>
              )}
              <Input
                label="Custom Load #"
                value={form.orderNumber}
                onChange={(e) => handleChange("orderNumber", e.value)}
                placeholder="Optional default"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Priority"
                value={form.priority}
                options={PRIORITY_OPTIONS}
                onChange={(e) => handleChange("priority", e.value)}
              />
              <Input
                label="Custom Job #"
                value={form.orderNumber}
                onChange={(e) => handleChange("orderNumber", e.value)}
                placeholder="Optional"
              />
              <Input
                label="Load Number"
                value={form.loadNumber}
                onChange={(e) => handleChange("loadNumber", e.value)}
                placeholder="Optional"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <label className="flex items-center gap-2 pt-8 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.useFlatRate}
                  onChange={(e) =>
                    handleChange("useFlatRate", e.target.checked)
                  }
                />
                Use flat rate
              </label>
              <Input
                label="Flat Rate"
                type="number"
                value={form.flatRate}
                onChange={(e) => handleChange("flatRate", e.value)}
              />
              <Input
                label="Base Rate"
                type="number"
                value={form.baseRate}
                onChange={(e) => handleChange("baseRate", e.value)}
              />
              <Input
                label="Fuel Rate"
                type="number"
                value={form.fuelRate}
                onChange={(e) => handleChange("fuelRate", e.value)}
              />
            </div>
          </>
        )}

        <TextArea
          label={isTrucking ? "Load Notes" : "Job Summary / Original Call"}
          value={form.summary || form.originalCall}
          onChange={(e) => {
            handleChange("summary", e.target.value);
            handleChange("originalCall", e.target.value);
          }}
          rows={3}
        />

        {!isTrucking && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">
                Service Items
              </h3>
              <Button
                size="xs"
                type="alternative"
                onClick={() => setItems([...items, emptyItem()])}
              >
                Add Item
              </Button>
            </div>
            {items.map((item, index) => (
              <div
                key={item._id || index}
                className="border rounded-lg p-3 space-y-3"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    label="Service Type"
                    value={item.serviceType}
                    onChange={(e) =>
                      handleItemChange(item._id, "serviceType", e.value)
                    }
                  />
                  <Input
                    label="Quoted Hours"
                    type="number"
                    value={item.quotedHours}
                    onChange={(e) =>
                      handleItemChange(
                        item._id,
                        "quotedHours",
                        Number(e.value)
                      )
                    }
                  />
                  <Select
                    label="Priority"
                    value={item.priority}
                    options={PRIORITY_OPTIONS}
                    onChange={(e) =>
                      handleItemChange(item._id, "priority", e.value)
                    }
                  />
                </div>
                <TextArea
                  label="Work Summary"
                  value={item.summary}
                  onChange={(e) =>
                    handleItemChange(item._id, "summary", e.target.value)
                  }
                  rows={2}
                />
              </div>
            ))}
          </div>
        )}

        {groupOptions.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              Task/Checklist Groups
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {groupOptions.map((group) => (
                <label key={group._id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.groupKey)}
                    onChange={() => handleToggleGroup(group.groupKey)}
                  />
                  {group.title}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default JobTemplateModal;
